<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useQueryClient } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';
import { useManageOrganisationStore } from '@/store/manageOrganisation.store';
import { ManageOrganisationMutation } from '@/mutations/manageOrganisation.mutations';

import { OrgContactMutation } from '@/mutations/orgContact.mutations';
import { OrgAddressMutation } from '@/mutations/orgAddress.mutations';
import { vatData } from '@/refs/commerce.refs';
import { formatTVA } from '@/utils/formatTVA.utils';
import { getShortFormeJuridique } from '@/utils/formeJuridique';
import { capitalizeWords } from '@/utils/capitalizeWords';
import type { ICreateOrganisationBody } from '@/types/manageOrgCommerce.types';

const router = useRouter();
const { t } = useI18n({ useScope: 'global' });
const queryClient = useQueryClient();
const organisationStore = useManageOrganisationStore();
const { handleCreateOrganisation, createOrganisation } =
  ManageOrganisationMutation();
const { handleCreateOrgContact } = OrgContactMutation();
const { handleCreateOrgAddress } = OrgAddressMutation();

// ── Options statiques ──────────────────────────────────────────────────────
const ORG_TYPES = computed(() => [
  { value: 'COMPANY', label: t('createClient.type.COMPANY') },
  { value: 'INDIVIDUAL', label: t('createClient.type.INDIVIDUAL') },
  { value: 'ASSOCIATION', label: t('createClient.type.ASSOCIATION') },
  { value: 'PUBLIC_ENTITY', label: t('createClient.type.PUBLIC_ENTITY') },
]);
const POSITIONS = computed(() => [
  {
    value: 'legal_representative',
    label: t('createClient.positions.legal_representative'),
  },
  { value: 'ceo', label: t('createClient.positions.ceo') },
  { value: 'cfo', label: t('createClient.positions.cfo') },
  { value: 'cto', label: t('createClient.positions.cto') },
  { value: 'manager', label: t('createClient.positions.manager') },
  { value: 'accountant', label: t('createClient.positions.accountant') },
  {
    value: 'technical_contact',
    label: t('createClient.positions.technical_contact'),
  },
  {
    value: 'billing_contact',
    label: t('createClient.positions.billing_contact'),
  },
  {
    value: 'support_contact',
    label: t('createClient.positions.support_contact'),
  },
]);
const ADDR_LABELS = computed(() => [
  { value: 'headquarters', label: t('createClient.addrLabels.headquarters') },
  { value: 'billing', label: t('createClient.addrLabels.billing') },
  { value: 'warehouse', label: t('createClient.addrLabels.warehouse') },
  { value: 'shipping', label: t('createClient.addrLabels.shipping') },
  { value: 'branch', label: t('createClient.addrLabels.branch') },
]);
const LANGUAGES = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
  { value: 'nl', label: 'Nederlands' },
];

// ── TVA lookup ─────────────────────────────────────────────────────────────
const vatInput = ref('');
const vatLoading = ref(false);
const vatError = ref<string | null>(null);
const vatFound = ref<any>(null);
const vatZoho = ref<any>(null);

async function checkVat() {
  const raw = vatInput.value.trim();
  if (raw.length < 4) {
    vatError.value = t('createClient.vat.errorShort');
    return;
  }
  vatError.value = null;
  vatLoading.value = true;
  vatFound.value = null;
  vatZoho.value = null;
  try {
    try {
      const taxId = raw.replace(/^BE/i, '');
      const org = await organisationStore.getOrganisationByTaxId(taxId);
      vatFound.value = (org as any)?.data ?? org;
      form.value.orgName = vatFound.value?.name ?? '';
      form.value.orgType = vatFound.value?.type ?? 'COMPANY';
      form.value.taxId = vatFound.value?.taxIdentificationNumber ?? raw;
      form.value.country = vatFound.value?.countryCode ?? '';
      vatLoading.value = false;
      return;
    } catch {
      /* pas en DB */
    }
    const res = await axiosInstance.get(
      `organizations/company-lookup/${encodeURIComponent(raw)}`,
      { headers: { 'x-client-app': 'admin-back-office' } },
    );
    const data = res.data as any;
    vatZoho.value = data;
    vatData.value = data;
    form.value.orgName = data.companyName ?? '';
    form.value.taxId = raw;
    form.value.orgType = 'COMPANY';
    form.value.addrLine1 = data.address ?? '';
  } catch (err: any) {
    if (err?.response?.status === 404) {
      vatError.value = t('createClient.vat.errorNotFound');
    } else {
      vatError.value = t('createClient.vat.errorConn');
    }
  } finally {
    vatLoading.value = false;
  }
}

function resetVat() {
  vatInput.value = '';
  vatFound.value = null;
  vatZoho.value = null;
  vatError.value = null;
  vatData.value = null;
  form.value.orgName = '';
  form.value.taxId = '';
  form.value.addrLine1 = '';
}

// ── ISO pays ───────────────────────────────────────────────────────────────
const ISO_FR: Record<string, string> = {
  AD: 'Andorre',
  AE: 'Émirats arabes unis',
  AF: 'Afghanistan',
  AG: 'Antigua-et-Barbuda',
  AI: 'Anguilla',
  AL: 'Albanie',
  AM: 'Arménie',
  AO: 'Angola',
  AR: 'Argentine',
  AS: 'Samoa américaines',
  AT: 'Autriche',
  AU: 'Australie',
  AW: 'Aruba',
  AX: 'Îles Åland',
  AZ: 'Azerbaïdjan',
  BA: 'Bosnie-Herzégovine',
  BB: 'Barbade',
  BD: 'Bangladesh',
  BE: 'Belgique',
  BF: 'Burkina Faso',
  BG: 'Bulgarie',
  BH: 'Bahreïn',
  BI: 'Burundi',
  BJ: 'Bénin',
  BL: 'Saint-Barthélemy',
  BM: 'Bermudes',
  BN: 'Brunéi',
  BO: 'Bolivie',
  BQ: 'Pays-Bas caribéens',
  BR: 'Brésil',
  BS: 'Bahamas',
  BT: 'Bhoutan',
  BW: 'Botswana',
  BY: 'Biélorussie',
  BZ: 'Belize',
  CA: 'Canada',
  CC: 'Îles Cocos',
  CD: 'Congo (RDC)',
  CF: 'Centrafrique',
  CG: 'Congo',
  CH: 'Suisse',
  CI: "Côte d'Ivoire",
  CK: 'Îles Cook',
  CL: 'Chili',
  CM: 'Cameroun',
  CN: 'Chine',
  CO: 'Colombie',
  CR: 'Costa Rica',
  CU: 'Cuba',
  CV: 'Cap-Vert',
  CW: 'Curaçao',
  CX: 'Île Christmas',
  CY: 'Chypre',
  CZ: 'Tchéquie',
  DE: 'Allemagne',
  DJ: 'Djibouti',
  DK: 'Danemark',
  DM: 'Dominique',
  DO: 'République dominicaine',
  DZ: 'Algérie',
  EC: 'Équateur',
  EE: 'Estonie',
  EG: 'Égypte',
  EH: 'Sahara occidental',
  ER: 'Érythrée',
  ES: 'Espagne',
  ET: 'Éthiopie',
  FI: 'Finlande',
  FJ: 'Fidji',
  FK: 'Îles Malouines',
  FM: 'Micronésie',
  FO: 'Îles Féroé',
  FR: 'France',
  GA: 'Gabon',
  GB: 'Royaume-Uni',
  GD: 'Grenade',
  GE: 'Géorgie',
  GF: 'Guyane française',
  GG: 'Guernesey',
  GH: 'Ghana',
  GI: 'Gibraltar',
  GL: 'Groenland',
  GM: 'Gambie',
  GN: 'Guinée',
  GP: 'Guadeloupe',
  GQ: 'Guinée équatoriale',
  GR: 'Grèce',
  GT: 'Guatemala',
  GU: 'Guam',
  GW: 'Guinée-Bissau',
  GY: 'Guyana',
  HK: 'Hong Kong',
  HN: 'Honduras',
  HR: 'Croatie',
  HT: 'Haïti',
  HU: 'Hongrie',
  ID: 'Indonésie',
  IE: 'Irlande',
  IL: 'Israël',
  IM: 'Île de Man',
  IN: 'Inde',
  IO: "Territoire britannique de l'océan Indien",
  IQ: 'Irak',
  IR: 'Iran',
  IS: 'Islande',
  IT: 'Italie',
  JE: 'Jersey',
  JM: 'Jamaïque',
  JO: 'Jordanie',
  JP: 'Japon',
  KE: 'Kenya',
  KG: 'Kirghizistan',
  KH: 'Cambodge',
  KI: 'Kiribati',
  KM: 'Comores',
  KN: 'Saint-Kitts-et-Nevis',
  KP: 'Corée du Nord',
  KR: 'Corée du Sud',
  KW: 'Koweït',
  KY: 'Îles Caïmans',
  KZ: 'Kazakhstan',
  LA: 'Laos',
  LB: 'Liban',
  LC: 'Sainte-Lucie',
  LI: 'Liechtenstein',
  LK: 'Sri Lanka',
  LR: 'Liberia',
  LS: 'Lesotho',
  LT: 'Lituanie',
  LU: 'Luxembourg',
  LV: 'Lettonie',
  LY: 'Libye',
  MA: 'Maroc',
  MC: 'Monaco',
  MD: 'Moldavie',
  ME: 'Monténégro',
  MF: 'Saint-Martin (partie française)',
  MG: 'Madagascar',
  MH: 'Îles Marshall',
  MK: 'Macédoine du Nord',
  ML: 'Mali',
  MM: 'Myanmar',
  MN: 'Mongolie',
  MO: 'Macao',
  MP: 'Îles Mariannes du Nord',
  MQ: 'Martinique',
  MR: 'Mauritanie',
  MS: 'Montserrat',
  MT: 'Malte',
  MU: 'Maurice',
  MV: 'Maldives',
  MW: 'Malawi',
  MX: 'Mexique',
  MY: 'Malaisie',
  MZ: 'Mozambique',
  NA: 'Namibie',
  NC: 'Nouvelle-Calédonie',
  NE: 'Niger',
  NF: 'Île Norfolk',
  NG: 'Nigéria',
  NI: 'Nicaragua',
  NL: 'Pays-Bas',
  NO: 'Norvège',
  NP: 'Népal',
  NR: 'Nauru',
  NU: 'Niue',
  NZ: 'Nouvelle-Zélande',
  OM: 'Oman',
  PA: 'Panama',
  PE: 'Pérou',
  PF: 'Polynésie française',
  PG: 'Papouasie-Nouvelle-Guinée',
  PH: 'Philippines',
  PK: 'Pakistan',
  PL: 'Pologne',
  PM: 'Saint-Pierre-et-Miquelon',
  PR: 'Porto Rico',
  PS: 'Palestine',
  PT: 'Portugal',
  PW: 'Palaos',
  PY: 'Paraguay',
  QA: 'Qatar',
  RE: 'La Réunion',
  RO: 'Roumanie',
  RS: 'Serbie',
  RU: 'Russie',
  RW: 'Rwanda',
  SA: 'Arabie saoudite',
  SB: 'Îles Salomon',
  SC: 'Seychelles',
  SD: 'Soudan',
  SE: 'Suède',
  SG: 'Singapour',
  SH: 'Sainte-Hélène',
  SI: 'Slovénie',
  SJ: 'Svalbard et Jan Mayen',
  SK: 'Slovaquie',
  SL: 'Sierra Leone',
  SM: 'Saint-Marin',
  SN: 'Sénégal',
  SO: 'Somalie',
  SR: 'Suriname',
  SS: 'Soudan du Sud',
  ST: 'Sao Tomé-et-Principe',
  SV: 'Salvador',
  SX: 'Saint-Martin (partie néerlandaise)',
  SY: 'Syrie',
  SZ: 'Eswatini',
  TC: 'Îles Turques-et-Caïques',
  TD: 'Tchad',
  TG: 'Togo',
  TH: 'Thaïlande',
  TJ: 'Tadjikistan',
  TK: 'Tokelau',
  TL: 'Timor oriental',
  TM: 'Turkménistan',
  TN: 'Tunisie',
  TO: 'Tonga',
  TR: 'Turquie',
  TT: 'Trinité-et-Tobago',
  TV: 'Tuvalu',
  TW: 'Taïwan',
  TZ: 'Tanzanie',
  UA: 'Ukraine',
  UG: 'Ouganda',
  US: 'États-Unis',
  UY: 'Uruguay',
  UZ: 'Ouzbékistan',
  VA: 'Vatican',
  VC: 'Saint-Vincent-et-les-Grenadines',
  VE: 'Venezuela',
  VG: 'Îles Vierges britanniques',
  VI: 'Îles Vierges américaines',
  VN: 'Viêt Nam',
  VU: 'Vanuatu',
  WF: 'Wallis-et-Futuna',
  WS: 'Samoa',
  XK: 'Kosovo',
  YE: 'Yémen',
  YT: 'Mayotte',
  ZA: 'Afrique du Sud',
  ZM: 'Zambie',
  ZW: 'Zimbabwe',
};

const countries = computed<{ code: string; nameFr: string }[]>(() =>
  Object.entries(ISO_FR)
    .map(([code, nameFr]) => ({ code, nameFr }))
    .sort((a, b) => a.nameFr.localeCompare(b.nameFr, 'fr')),
);

const countryOrgOpen = ref(false);
const countryOrgSearch = ref('');
const countryAddrOpen = ref(false);
const countryAddrSearch = ref('');

function filteredCountries(search: string) {
  const q = search.toLowerCase();
  return q
    ? countries.value.filter((c) => c.nameFr.toLowerCase().includes(q))
    : countries.value;
}
function countryLabel(code: string) {
  return ISO_FR[code] ?? code;
}

function selectCountryOrg(code: string) {
  form.value.country = code;
  countryOrgOpen.value = false;
  countryOrgSearch.value = '';
}
function selectCountryAddr(code: string) {
  form.value.addrCountry = code;
  countryAddrOpen.value = false;
  countryAddrSearch.value = '';
}

// ── Formulaire ─────────────────────────────────────────────────────────────
const form = ref({
  orgName: '',
  orgType: 'COMPANY',
  taxId: '',
  description: '',
  country: '',
  addrLine1: '',
  addrLine2: '',
  addrCity: '',
  addrPostal: '',
  addrCountry: '',
  addrLabel: 'headquarters',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  position: 'legal_representative',
  languages: ['fr'] as string[],
});

function toggleLanguage(lang: string) {
  const idx = form.value.languages.indexOf(lang);
  if (idx >= 0) {
    if (form.value.languages.length > 1) form.value.languages.splice(idx, 1);
  } else form.value.languages.push(lang);
}

const canSubmit = computed(
  () =>
    (vatFound.value || vatZoho.value) &&
    form.value.orgName.trim() &&
    form.value.country &&
    form.value.lastName.trim() &&
    form.value.firstName.trim() &&
    form.value.addrLine1.trim() &&
    form.value.addrCity.trim() &&
    form.value.addrPostal.trim() &&
    form.value.addrCountry,
);

// ── Soumission — 3 appels séparés ──────────────────────────────────────────
const submitError = ref<string | null>(null);
const submitSuccess = ref(false);
const createdId = ref<string | null>(null);
const isSaving = ref(false);

async function submit() {
  if (!canSubmit.value || isSaving.value) return;
  submitError.value = null;
  isSaving.value = true;
  try {
    // 1. Créer l'organisation
    const orgBody: ICreateOrganisationBody = {
      name: form.value.orgName,
      type: form.value.orgType as any,
      taxIdentificationNumber: formatTVA(form.value.taxId),
      description: form.value.description || undefined,
      country: form.value.country,
    };
    const orgData = await handleCreateOrganisation(orgBody);
    const orgId = orgData?.data?.id ?? orgData?.id ?? null;
    createdId.value = orgId;

    if (orgId) {
      // 2. Créer le contact via endpoint dédié
      await handleCreateOrgContact(orgId, {
        firstName: form.value.firstName,
        lastName: form.value.lastName,
        email: form.value.email || undefined,
        phone: form.value.phone || undefined,
        position: form.value.position as any,
        isPrimary: true,
      });

      // 3. Créer l'adresse via endpoint dédié
      await handleCreateOrgAddress(orgId, {
        addressLine1: form.value.addrLine1,
        addressLine2: form.value.addrLine2 || undefined,
        city: form.value.addrCity,
        postalCode: form.value.addrPostal,
        country: form.value.addrCountry,
        isPrimary: true,
        label: form.value.addrLabel as any,
      });
    }

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['clients-page-orgs'] }),
      queryClient.invalidateQueries({ queryKey: ['GetOrganisations'] }),
    ]);
    submitSuccess.value = true;
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    submitError.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? err?.message ?? 'Une erreur est survenue');
  } finally {
    isSaving.value = false;
  }
}

function goToClient() {
  if (createdId.value) router.push(`/clients/${createdId.value}`);
  else router.push('/clients');
}
</script>

<template>
  <div
    class="cnp"
    @click.self="
      countryOrgOpen = false;
      countryAddrOpen = false;
    "
  >
    <!-- Breadcrumb -->
    <nav class="cnp__breadcrumb">
      <button class="cnp__bc-link" @click="router.push('/clients')">
        {{ t('createClient.breadcrumb') }}
      </button>
      <i class="pi pi-angle-right cnp__bc-sep" />
      <span class="cnp__bc-cur">{{ t('createClient.bcCur') }}</span>
    </nav>

    <!-- ── Succès ───────────────────────────────────────────────────────────── -->
    <div v-if="submitSuccess" class="cnp__success">
      <div class="cnp__success-icon"><i class="pi pi-check" /></div>
      <h2 class="cnp__success-title">{{ t('createClient.success.title') }}</h2>
      <p class="cnp__success-sub">{{ t('createClient.success.sub') }}</p>
      <div class="cnp__success-actions">
        <button
          class="cnp__btn cnp__btn--ghost"
          @click="router.push('/clients')"
        >
          <i class="pi pi-arrow-left" />
          {{ t('createClient.success.backList') }}
        </button>
        <button class="cnp__btn cnp__btn--primary" @click="goToClient">
          <i class="pi pi-eye" /> {{ t('createClient.success.viewClient') }}
        </button>
      </div>
    </div>

    <!-- ── Formulaire ───────────────────────────────────────────────────────── -->
    <template v-else>
      <div class="cnp__header">
        <button
          class="cnp__back"
          @click="router.push('/clients')"
          :title="t('createClient.back')"
        >
          <i class="pi pi-arrow-left" />
        </button>
        <div>
          <h1 class="cnp__title">{{ t('createClient.title') }}</h1>
          <p class="cnp__sub">{{ t('createClient.sub') }}</p>
        </div>
      </div>

      <div class="cnp__body">
        <!-- ═══════ 1. Identification TVA ═══════ -->
        <section class="cnp__section">
          <div class="cnp__section-header">
            <div class="cnp__section-icon"><i class="pi pi-search" /></div>
            <div>
              <p class="cnp__section-title">
                {{ t('createClient.vat.sectionTitle') }}
              </p>
              <p class="cnp__section-sub">
                {{ t('createClient.vat.sectionSub') }}
              </p>
            </div>
          </div>
          <div class="cnp__input-row">
            <input
              v-model="vatInput"
              class="cnp__input cnp__input--grow"
              type="text"
              placeholder="Ex: BE0123456789"
              @keydown.enter="checkVat"
              :disabled="!!vatFound || !!vatZoho"
            />
            <button
              v-if="!vatFound && !vatZoho"
              class="cnp__btn cnp__btn--primary"
              @click="checkVat"
              :disabled="vatLoading"
            >
              <span v-if="vatLoading" class="cnp__spinner" />
              <i v-else class="pi pi-search" />
              {{
                vatLoading
                  ? t('createClient.vat.searching')
                  : t('createClient.vat.verify')
              }}
            </button>
            <button v-else class="cnp__btn cnp__btn--ghost" @click="resetVat">
              <i class="pi pi-times" /> {{ t('createClient.vat.clear') }}
            </button>
          </div>
          <p v-if="vatError" class="cnp__field-error">
            <i class="pi pi-exclamation-circle" /> {{ vatError }}
          </p>
          <div v-if="vatFound" class="cnp__found-card cnp__found-card--warn">
            <span class="cnp__found-badge cnp__found-badge--warn"
              ><i class="pi pi-exclamation-triangle" />
              {{ t('createClient.vat.alreadyBadge') }}</span
            >
            <p class="cnp__found-name">{{ capitalizeWords(vatFound.name) }}</p>
            <p class="cnp__found-meta">
              {{ vatFound.taxIdentificationNumber }}
            </p>
          </div>
          <div v-else-if="vatZoho" class="cnp__found-card cnp__found-card--ok">
            <span class="cnp__found-badge cnp__found-badge--ok"
              ><i class="pi pi-check-circle" />
              {{ t('createClient.vat.foundBadge') }}</span
            >
            <p class="cnp__found-name">{{ vatZoho.nom_societe }}</p>
            <p class="cnp__found-meta">
              {{ getShortFormeJuridique(vatZoho.forme_juridique)
              }}<template v-if="vatZoho.adresse">
                · {{ vatZoho.adresse }}</template
              >
            </p>
          </div>
        </section>

        <div class="cnp__divider" />

        <!-- ═══════ 2. Organisation ═══════ -->
        <section class="cnp__section">
          <div class="cnp__section-header">
            <div class="cnp__section-icon"><i class="pi pi-building" /></div>
            <div>
              <p class="cnp__section-title">
                {{ t('createClient.org.sectionTitle') }}
              </p>
              <p class="cnp__section-sub">
                {{ t('createClient.org.sectionSub') }}
              </p>
            </div>
          </div>
          <div class="cnp__fields">
            <div class="cnp__field-row">
              <div class="cnp__field cnp__field--span2">
                <label class="cnp__label"
                  >{{ t('createClient.org.name') }}
                  <span class="cnp__req">*</span></label
                >
                <input
                  v-model="form.orgName"
                  class="cnp__input"
                  type="text"
                  placeholder="Acme Belgium SA"
                />
              </div>
            </div>
            <div class="cnp__field-row">
              <div class="cnp__field">
                <label class="cnp__label">{{
                  t('createClient.org.orgType')
                }}</label>
                <div class="cnp__select-wrap">
                  <select v-model="form.orgType" class="cnp__select">
                    <option
                      v-for="opt in ORG_TYPES"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                  <i class="pi pi-chevron-down cnp__select-icon" />
                </div>
              </div>
              <div class="cnp__field">
                <label class="cnp__label">{{
                  t('createClient.org.tax')
                }}</label>
                <input
                  v-model="form.taxId"
                  class="cnp__input"
                  type="text"
                  placeholder="BE0123456789"
                />
              </div>
            </div>
            <div class="cnp__field-row">
              <div class="cnp__field">
                <label class="cnp__label"
                  >{{ t('createClient.org.country') }}
                  <span class="cnp__req">*</span></label
                >
                <div class="cnp__dd" @click.stop>
                  <button
                    type="button"
                    class="cnp__dd-trigger"
                    :class="{ 'cnp__dd-trigger--open': countryOrgOpen }"
                    @click="
                      countryOrgOpen = !countryOrgOpen;
                      countryAddrOpen = false;
                    "
                  >
                    <span :class="form.country ? '' : 'cnp__dd-placeholder'">{{
                      form.country
                        ? countryLabel(form.country)
                        : t('createClient.org.selectCountry')
                    }}</span>
                    <i
                      class="pi pi-chevron-down cnp__dd-arrow"
                      :class="{ 'cnp__dd-arrow--open': countryOrgOpen }"
                    />
                  </button>
                  <div v-if="countryOrgOpen" class="cnp__dd-panel">
                    <div class="cnp__dd-search-wrap">
                      <i class="pi pi-search cnp__dd-search-icon" />
                      <input
                        v-model="countryOrgSearch"
                        class="cnp__dd-search"
                        type="text"
                        :placeholder="t('createClient.org.searchCountry')"
                        @click.stop
                        autofocus
                      />
                    </div>
                    <ul class="cnp__dd-list">
                      <li
                        v-for="c in filteredCountries(countryOrgSearch)"
                        :key="c.code"
                        class="cnp__dd-item"
                        :class="{
                          'cnp__dd-item--active': form.country === c.code,
                        }"
                        @click="selectCountryOrg(c.code)"
                      >
                        {{ c.nameFr }}
                      </li>
                      <li
                        v-if="filteredCountries(countryOrgSearch).length === 0"
                        class="cnp__dd-empty"
                      >
                        {{ t('createClient.org.noResult') }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div class="cnp__field">
                <label class="cnp__label">{{
                  t('createClient.org.description')
                }}</label>
                <input
                  v-model="form.description"
                  class="cnp__input"
                  type="text"
                  :placeholder="t('createClient.org.descPlaceholder')"
                />
              </div>
            </div>
          </div>
        </section>

        <div class="cnp__divider" />

        <!-- ═══════ 3. Adresse principale ═══════ -->
        <section class="cnp__section">
          <div class="cnp__section-header">
            <div class="cnp__section-icon"><i class="pi pi-map-marker" /></div>
            <div>
              <p class="cnp__section-title">
                {{ t('createClient.addr.sectionTitle') }}
              </p>
              <p class="cnp__section-sub">
                {{ t('createClient.addr.sectionSub') }}
              </p>
            </div>
          </div>
          <div class="cnp__fields">
            <div class="cnp__field-row">
              <div class="cnp__field">
                <label class="cnp__label"
                  >{{ t('createClient.addr.line1') }}
                  <span class="cnp__req">*</span></label
                >
                <input
                  v-model="form.addrLine1"
                  class="cnp__input"
                  type="text"
                  placeholder="Rue de la Loi 1"
                />
              </div>
              <div class="cnp__field">
                <label class="cnp__label">{{
                  t('createClient.addr.line2')
                }}</label>
                <input
                  v-model="form.addrLine2"
                  class="cnp__input"
                  type="text"
                  :placeholder="t('createClient.addr.line2Placeholder')"
                />
              </div>
            </div>
            <div class="cnp__field-row">
              <div class="cnp__field">
                <label class="cnp__label"
                  >{{ t('createClient.addr.city') }}
                  <span class="cnp__req">*</span></label
                >
                <input
                  v-model="form.addrCity"
                  class="cnp__input"
                  type="text"
                  placeholder="Bruxelles"
                />
              </div>
              <div class="cnp__field">
                <label class="cnp__label"
                  >{{ t('createClient.addr.postal') }}
                  <span class="cnp__req">*</span></label
                >
                <input
                  v-model="form.addrPostal"
                  class="cnp__input"
                  type="text"
                  placeholder="1000"
                />
              </div>
            </div>
            <div class="cnp__field-row">
              <div class="cnp__field">
                <label class="cnp__label"
                  >{{ t('createClient.addr.country') }}
                  <span class="cnp__req">*</span></label
                >
                <div class="cnp__dd" @click.stop>
                  <button
                    type="button"
                    class="cnp__dd-trigger"
                    :class="{ 'cnp__dd-trigger--open': countryAddrOpen }"
                    @click="
                      countryAddrOpen = !countryAddrOpen;
                      countryOrgOpen = false;
                    "
                  >
                    <span
                      :class="form.addrCountry ? '' : 'cnp__dd-placeholder'"
                      >{{
                        form.addrCountry
                          ? countryLabel(form.addrCountry)
                          : t('createClient.addr.selectCountry')
                      }}</span
                    >
                    <i
                      class="pi pi-chevron-down cnp__dd-arrow"
                      :class="{ 'cnp__dd-arrow--open': countryAddrOpen }"
                    />
                  </button>
                  <div v-if="countryAddrOpen" class="cnp__dd-panel">
                    <div class="cnp__dd-search-wrap">
                      <i class="pi pi-search cnp__dd-search-icon" />
                      <input
                        v-model="countryAddrSearch"
                        class="cnp__dd-search"
                        type="text"
                        :placeholder="t('createClient.addr.selectCountry')"
                        @click.stop
                        autofocus
                      />
                    </div>
                    <ul class="cnp__dd-list">
                      <li
                        v-for="c in filteredCountries(countryAddrSearch)"
                        :key="c.code"
                        class="cnp__dd-item"
                        :class="{
                          'cnp__dd-item--active': form.addrCountry === c.code,
                        }"
                        @click="selectCountryAddr(c.code)"
                      >
                        {{ c.nameFr }}
                      </li>
                      <li
                        v-if="filteredCountries(countryAddrSearch).length === 0"
                        class="cnp__dd-empty"
                      >
                        {{ t('createClient.addr.noResult') }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div class="cnp__field">
                <label class="cnp__label">{{
                  t('createClient.addr.addrType')
                }}</label>
                <div class="cnp__select-wrap">
                  <select v-model="form.addrLabel" class="cnp__select">
                    <option
                      v-for="l in ADDR_LABELS"
                      :key="l.value"
                      :value="l.value"
                    >
                      {{ l.label }}
                    </option>
                  </select>
                  <i class="pi pi-chevron-down cnp__select-icon" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <div class="cnp__divider" />

        <!-- ═══════ 4. Contact principal ═══════ -->
        <section class="cnp__section">
          <div class="cnp__section-header">
            <div class="cnp__section-icon"><i class="pi pi-user" /></div>
            <div>
              <p class="cnp__section-title">
                {{ t('createClient.contact.sectionTitle') }}
              </p>
              <p class="cnp__section-sub">
                {{ t('createClient.contact.sectionSub') }}
              </p>
            </div>
          </div>
          <div class="cnp__fields">
            <div class="cnp__field-row">
              <div class="cnp__field">
                <label class="cnp__label"
                  >{{ t('createClient.contact.lastName') }}
                  <span class="cnp__req">*</span></label
                >
                <input
                  v-model="form.lastName"
                  class="cnp__input"
                  type="text"
                  placeholder="Dupont"
                />
              </div>
              <div class="cnp__field">
                <label class="cnp__label"
                  >{{ t('createClient.contact.firstName') }}
                  <span class="cnp__req">*</span></label
                >
                <input
                  v-model="form.firstName"
                  class="cnp__input"
                  type="text"
                  placeholder="Jean"
                />
              </div>
            </div>
            <div class="cnp__field-row">
              <div class="cnp__field">
                <label class="cnp__label">{{
                  t('createClient.contact.email')
                }}</label>
                <input
                  v-model="form.email"
                  class="cnp__input"
                  type="email"
                  placeholder="jean.dupont@exemple.com"
                />
              </div>
              <div class="cnp__field">
                <label class="cnp__label">{{
                  t('createClient.contact.phone')
                }}</label>
                <input
                  v-model="form.phone"
                  class="cnp__input"
                  type="tel"
                  placeholder="+32 470 00 00 00"
                />
              </div>
            </div>
            <div class="cnp__field-row">
              <div class="cnp__field">
                <label class="cnp__label">{{
                  t('createClient.contact.position')
                }}</label>
                <div class="cnp__select-wrap">
                  <select v-model="form.position" class="cnp__select">
                    <option
                      v-for="p in POSITIONS"
                      :key="p.value"
                      :value="p.value"
                    >
                      {{ p.label }}
                    </option>
                  </select>
                  <i class="pi pi-chevron-down cnp__select-icon" />
                </div>
              </div>
              <div class="cnp__field">
                <label class="cnp__label">{{
                  t('createClient.contact.languages')
                }}</label>
                <div class="cnp__lang-btns">
                  <button
                    v-for="l in LANGUAGES"
                    :key="l.value"
                    type="button"
                    class="cnp__lang-btn"
                    :class="{
                      'cnp__lang-btn--active': form.languages.includes(l.value),
                    }"
                    @click="toggleLanguage(l.value)"
                  >
                    {{ l.label }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Erreur soumission -->
        <div v-if="submitError" class="cnp__error-banner">
          <i class="pi pi-exclamation-circle" /> {{ submitError }}
        </div>

        <!-- Actions -->
        <div class="cnp__actions">
          <button
            class="cnp__btn cnp__btn--ghost"
            @click="router.push('/clients')"
          >
            <i class="pi pi-times" /> {{ t('createClient.cancel') }}
          </button>
          <button
            class="cnp__btn cnp__btn--primary"
            :disabled="!canSubmit || isSaving"
            @click="submit"
          >
            <span v-if="isSaving" class="cnp__spinner cnp__spinner--white" />
            <i v-else class="pi pi-check" />
            {{
              isSaving ? t('createClient.submitting') : t('createClient.submit')
            }}
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.cnp {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}
@media (max-width: 1023px) {
  .cnp {
    padding: 1.5rem;
  }
}
@media (max-width: 639px) {
  .cnp {
    padding: 1rem;
    gap: 1rem;
  }
}

.cnp__breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.cnp__bc-link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface-muted);
  transition: color 0.15s;
}
.cnp__bc-link:hover {
  color: var(--nxt-primary);
}
.cnp__bc-sep {
  font-size: 0.625rem;
}
.cnp__bc-cur {
  font-weight: 700;
  color: var(--nxt-on-surface);
}

.cnp__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.5rem;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}
.cnp__back {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  transition: all 0.15s;
}
.cnp__back:hover {
  background: var(--nxt-surface-mid);
  border-color: var(--nxt-outline);
  color: var(--nxt-on-surface);
}
.cnp__title {
  margin: 0 0 0.15rem;
  font-size: 1.125rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--nxt-on-surface);
}
.cnp__sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}

.cnp__body {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.5rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.cnp__section {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.cnp__section-header {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
}
.cnp__section-icon {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.625rem;
  flex-shrink: 0;
  background: var(--nxt-surface-mid);
  border: 1px solid var(--nxt-outline-variant);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9375rem;
  color: var(--nxt-on-surface-variant);
}
.cnp__section-title {
  margin: 0 0 0.1rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.cnp__section-sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.cnp__divider {
  height: 1px;
  background: var(--nxt-outline-variant);
}

.cnp__fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.cnp__field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
.cnp__field--span2 {
  grid-column: span 2;
}
@media (max-width: 620px) {
  .cnp__field-row {
    grid-template-columns: 1fr;
  }
  .cnp__field--span2 {
    grid-column: span 1;
  }
}
.cnp__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.cnp__label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
}
.cnp__req {
  color: var(--nxt-error);
}

.cnp__input-row {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}
.cnp__input--grow {
  flex: 1;
}
.cnp__input {
  height: 2.625rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  transition:
    border-color 0.15s,
    background 0.15s;
  width: 100%;
  box-sizing: border-box;
}
.cnp__input::placeholder {
  color: var(--nxt-on-surface-muted);
}
.cnp__input:focus {
  border-color: var(--nxt-primary);
  background: var(--nxt-card-bg);
}
.cnp__input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cnp__select-wrap {
  position: relative;
}
.cnp__select {
  width: 100%;
  height: 2.625rem;
  padding: 0 2.5rem 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  transition: border-color 0.15s;
}
.cnp__select:focus {
  border-color: var(--nxt-primary);
}
.cnp__select-icon {
  position: absolute;
  right: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  pointer-events: none;
}

.cnp__dd {
  position: relative;
}
.cnp__dd-trigger {
  width: 100%;
  height: 2.625rem;
  padding: 0 2.5rem 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.cnp__dd-trigger--open,
.cnp__dd-trigger:focus {
  border-color: var(--nxt-primary);
}
.cnp__dd-placeholder {
  color: var(--nxt-on-surface-muted);
}
.cnp__dd-arrow {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  transition: transform 0.2s;
  flex-shrink: 0;
}
.cnp__dd-arrow--open {
  transform: rotate(180deg);
}
.cnp__dd-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 200;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}
.cnp__dd-search-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.cnp__dd-search-icon {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  flex-shrink: 0;
}
.cnp__dd-search {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.875rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
}
.cnp__dd-search::placeholder {
  color: var(--nxt-on-surface-muted);
}
.cnp__dd-list {
  list-style: none;
  margin: 0;
  padding: 0.375rem;
  max-height: 220px;
  overflow-y: auto;
}
.cnp__dd-item {
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: var(--nxt-on-surface);
  transition: background 0.1s;
}
.cnp__dd-item:hover {
  background: var(--nxt-surface-mid);
}
.cnp__dd-item--active {
  background: var(--nxt-surface-mid);
  font-weight: 700;
  color: var(--nxt-primary);
}
.cnp__dd-empty {
  padding: 0.75rem;
  text-align: center;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}

.cnp__lang-btns {
  display: flex;
  gap: 0.5rem;
  height: 2.625rem;
  align-items: center;
}
.cnp__lang-btn {
  height: 2rem;
  padding: 0 0.875rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;
}
.cnp__lang-btn--active {
  background: var(--nxt-primary);
  border-color: var(--nxt-primary);
  color: var(--nxt-on-primary);
}

.cnp__found-card {
  padding: 1rem 1.25rem;
  border-radius: 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.cnp__found-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.175rem 0.5rem;
  border-radius: 0.3rem;
  width: fit-content;
  margin-bottom: 0.25rem;
}
.cnp__found-badge--ok {
  background: var(--nxt-status-up-bg);
  color: var(--nxt-status-up);
}
.cnp__found-badge--warn {
  background: rgba(0, 0, 0, 0.1);
  color: var(--nxt-primary);
}
.cnp__found-name {
  font-size: 1rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  margin: 0;
}
.cnp__found-meta {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  margin: 0;
}
.cnp__field-error {
  font-size: 0.8125rem;
  color: var(--nxt-error);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.cnp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.5rem;
  padding: 0 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  border: none;
  transition:
    filter 0.15s,
    opacity 0.15s;
}
.cnp__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.cnp__btn--primary {
  background: var(--nxt-gradient-primary);
  color: var(--nxt-on-primary);
  box-shadow: var(--nxt-gradient-primary-btn-shadow);
}
.cnp__btn--primary:not(:disabled):hover {
  filter: brightness(1.07);
}
.cnp__btn--ghost {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
.cnp__btn--ghost:hover {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface);
}

.cnp__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--nxt-outline-variant);
}

.cnp__error-banner {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.875rem 1.125rem;
  border-radius: 0.75rem;
  background: var(--nxt-error-container);
  border: 1px solid var(--nxt-on-error-container);
  font-size: 0.875rem;
  color: var(--nxt-on-error-container);
}

.cnp__spinner {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid var(--nxt-outline-variant);
  border-top-color: var(--nxt-primary);
  animation: cnp-spin 0.7s linear infinite;
}
.cnp__spinner--white {
  border-color: rgba(255, 255, 255, 0.25);
  border-top-color: var(--nxt-on-primary);
}
@keyframes cnp-spin {
  to {
    transform: rotate(360deg);
  }
}

.cnp__success {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 4rem 2rem;
  text-align: center;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.5rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
.cnp__success-icon {
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: var(--nxt-status-up-bg);
  border: 2px solid var(--nxt-status-up);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--nxt-status-up);
}
.cnp__success-title {
  margin: 0;
  font-size: 1.375rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.cnp__success-sub {
  margin: 0;
  font-size: 0.9375rem;
  color: var(--nxt-on-surface-muted);
}
.cnp__success-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
  justify-content: center;
}
</style>

<style>
[data-theme='dark'] .cnp {
  background: #0c1421;
}
[data-theme='dark'] .cnp__header,
[data-theme='dark'] .cnp__body,
[data-theme='dark'] .cnp__success {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cnp__section-icon {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cnp__divider {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cnp__input,
[data-theme='dark'] .cnp__select,
[data-theme='dark'] .cnp__dd-trigger {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .cnp__input:focus,
[data-theme='dark'] .cnp__select:focus,
[data-theme='dark'] .cnp__dd-trigger--open,
[data-theme='dark'] .cnp__dd-trigger:focus {
  border-color: #ffd60a;
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cnp__dd-panel {
  background: #111c2e;
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .cnp__dd-item:hover,
[data-theme='dark'] .cnp__dd-item--active {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cnp__dd-item--active {
  color: #ffd60a;
}
[data-theme='dark'] .cnp__lang-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .cnp__lang-btn--active {
  background: #ffd60a;
  border-color: #ffd60a;
  color: #0b1320;
}
[data-theme='dark'] .cnp__found-card {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .cnp__btn--ghost {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .cnp__btn--ghost:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .cnp__back {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .cnp__back:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
</style>
